import {
  NameStepsEnumPartner,
  OrgTypeEnum,
  NamesOfFieldEnum,
  FormData,
  ClientId,
  AccountDetailsFormFieldsPartner,
  SharedTypeRequestFields,
  PayformDetailsFormFieldsPartner,
  CompanyDetailsFormFieldsPartner
} from "~/typings/types";
import { getValuesFromObject } from "~/utils/getValuesFromObject";
import { getWithPrefixPhone } from "~/utils/getWithPrefixPhone";
import {
  postDraftCompanyDetails,
  postDraftAccountDetails,
  ReqPostArguments,
  ResPost,
  postDraftPayformDetails
} from "./apiMethods";
import { cleanText, getFieldNameWithCondition } from "~/utils";
import { defaultAxiosRequestConfig } from "~/api/apiMethods";
import { SanitizationRules, sanitizeDataObject } from "~/pages/questionnaires/v2/customer/api/sanitizeDataObject";

type RequestFunction = ({
  data,
  config
}: ReqPostArguments<SharedTypeRequestFields>) => Promise<ResPost>;
type RequestConfig = Omit<
  Record<NameStepsEnumPartner, RequestFunction>,
  NameStepsEnumPartner.AttachmentsDetails
>;

const requestConfig: RequestConfig = {
  [NameStepsEnumPartner.CompanyDetails]: postDraftCompanyDetails,
  [NameStepsEnumPartner.AccountDetails]: postDraftAccountDetails,
  [NameStepsEnumPartner.PayformDetails]: postDraftPayformDetails
};

interface ApiService {
  values: FormData;
  pageId: Exclude<NameStepsEnumPartner, NameStepsEnumPartner.AttachmentsDetails>;
  clientId: ClientId;
  orgType: OrgTypeEnum;
  customSanitizationRules?: SanitizationRules;
  isProdamusPayClient?: boolean;
  isAcceptPolitics?: boolean;
  isAcceptPrivacy?: boolean;
  isAcceptPromo?: boolean;

}

export const apiServicePartner = async ({ values, pageId, clientId, orgType, customSanitizationRules, isProdamusPayClient, isAcceptPolitics, isAcceptPrivacy, isAcceptPromo }: ApiService) =>
  await new Promise((resolve, reject) => {
    let reqData: any; // TODO: fixed any type

    if (pageId === NameStepsEnumPartner.CompanyDetails) {
      reqData = {
        id: cleanText(clientId),
        ui_version: "v1",
        fields: sanitizeDataObject({
          [NamesOfFieldEnum.OrgType]: orgType,
          ...(orgType === OrgTypeEnum.IndividualEntrepreneur || orgType === OrgTypeEnum.Company
            ? {
                [NamesOfFieldEnum.VatMode]: values?.[NamesOfFieldEnum.VatMode],
                [NamesOfFieldEnum.Vat]:
                  values?.[NamesOfFieldEnum.IsVat] === "no"
                    ? "no"
                    : values?.[NamesOfFieldEnum.Vat]
              }
            : {}),
          ...getValuesFromObject<
            Omit<
              CompanyDetailsFormFieldsPartner,
              | NamesOfFieldEnum.OrgType
              | NamesOfFieldEnum.VatMode
              | NamesOfFieldEnum.Vat
              | NamesOfFieldEnum.IsVat
            >
          >({
            object: values,
            values: [
              NamesOfFieldEnum.Inn,
              NamesOfFieldEnum.BankBic,
              NamesOfFieldEnum.BankName,
              NamesOfFieldEnum.SettlementAccount,
              NamesOfFieldEnum.CorrespondentAccount,
              NamesOfFieldEnum.LegalAddress,
              NamesOfFieldEnum.LegalAddressFiasId,
              // NamesOfFieldEnum.IsWantCreatePayform,
              getFieldNameWithCondition(orgType === OrgTypeEnum.Company, NamesOfFieldEnum.Ogrn),
              getFieldNameWithCondition(orgType === OrgTypeEnum.Company, NamesOfFieldEnum.Kpp),
              getFieldNameWithCondition(
                orgType === OrgTypeEnum.IndividualEntrepreneur,
                NamesOfFieldEnum.Ogrnip
              )
            ]
          })
        }, customSanitizationRules)
      };
    }

    if (pageId === NameStepsEnumPartner.AccountDetails) {
      reqData = {
        id: cleanText(clientId),
        ui_version: "v1",
        fields: sanitizeDataObject({
          [NamesOfFieldEnum.OrgType]: orgType,
          [NamesOfFieldEnum.PartnerConnectionWay]: 'with_discount',
          [NamesOfFieldEnum.IsProdamusPayClient]: isProdamusPayClient,
          ...(isProdamusPayClient ? {
            [NamesOfFieldEnum.IsOffertAccepted]: isAcceptPolitics ? 1 : 0,
            // privacy-чекбокс убран из partner-UI по ТЗ https://jira.tasktracker.help/browse/LKP-3640
            // [NamesOfFieldEnum.IsPrivacyAccepted]: 1,
            [NamesOfFieldEnum.IsPromoAccepted]: isAcceptPromo ? 1 : 0,
          } : {}),
          [NamesOfFieldEnum.CrmPhone]: getWithPrefixPhone(values?.[NamesOfFieldEnum.CrmPhone]),
          ...getValuesFromObject<
            Omit<
              AccountDetailsFormFieldsPartner,
              NamesOfFieldEnum.OrgType | NamesOfFieldEnum.CrmPhone
            >
          >({
            object: values,
            values: [
              NamesOfFieldEnum.CrmName,
              NamesOfFieldEnum.Name,
              NamesOfFieldEnum.CrmMessengerLink,
              NamesOfFieldEnum.CrmEmail,
              NamesOfFieldEnum.CrmSocialLinks,
              NamesOfFieldEnum.CrmOccupation,
              NamesOfFieldEnum.PromocodeReferral,
              NamesOfFieldEnum.PromocodeOwn,
              NamesOfFieldEnum.PartnerLevel,
              NamesOfFieldEnum.Partnerships,

            ]
          })
        }, customSanitizationRules)
      };
    }

    if (pageId === NameStepsEnumPartner.PayformDetails) {
      reqData = {
        id: cleanText(clientId),
        ui_version: "v1",
        fields: sanitizeDataObject({
          [NamesOfFieldEnum.OrgType]: orgType,
          [NamesOfFieldEnum.PayformName]: `https://${values?.payform_name}.payform.ru`,
          ...getValuesFromObject<
            Omit<
              PayformDetailsFormFieldsPartner,
              NamesOfFieldEnum.OrgType | NamesOfFieldEnum.PayformName
            >
          >({
            object: values,
            values: [
              NamesOfFieldEnum.ProductName,
              NamesOfFieldEnum.ProductType,
              NamesOfFieldEnum.ProductDescription,
              NamesOfFieldEnum.PayformLinks,
              NamesOfFieldEnum.ShopName
            ]
          })
        }, customSanitizationRules)
      };
    }

    requestConfig?.[pageId]({ data: reqData, config: defaultAxiosRequestConfig })
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });
